import { useState, useEffect } from "react";
import { T, S, fmtDate } from "./theme";
import { Badge, Btn, Drawer, DrawerSection } from "./components";
import { fetchStarRatings } from "./api";

const SCORE_COLORS = { 5: T.green, 4: T.green, 3: T.orange, 2: T.red, 1: T.red };
const SCORE_LABELS = { 5: "Excellent", 4: "Good", 3: "Neutral", 2: "Poor", 1: "Critical" };
const SCORE_BADGES = { 5: "green", 4: "green", 3: "orange", 2: "red", 1: "red" };

export default function StarRatings({ showToast }) {
  const [ratings, setRatings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [scoreFilter, setScoreFilter] = useState("");
  const [orgFilter, setOrgFilter] = useState("");
  const [page, setPage] = useState(1);
  const [drawer, setDrawer] = useState(null);

  useEffect(() => { loadRatings(); }, []);

  const loadRatings = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchStarRatings({ limit: 500 });
      setRatings(res.data || []);
      setStats(res.stats || null);
    } catch (err) {
      console.error("Failed to load star ratings:", err);
      setError("Failed to load ratings from Pylon.");
    } finally {
      setLoading(false);
    }
  };

  const allOrgs = [...new Set(ratings.map((r) => r.org).filter(Boolean))].sort();

  const filtered = ratings.filter((r) => {
    if (scoreFilter && r.score !== parseInt(scoreFilter)) return false;
    if (orgFilter && r.org !== orgFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (
        !(r.title || "").toLowerCase().includes(q) &&
        !(r.rated_user || "").toLowerCase().includes(q) &&
        !(r.org || "").toLowerCase().includes(q) &&
        !(r.requester || "").toLowerCase().includes(q) &&
        !String(r.number).includes(q)
      ) return false;
    }
    return true;
  });

  const perPage = 25;
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

  if (loading) {
    return <div style={{ padding: 40, textAlign: "center", color: T.textDim }}>Loading star ratings...</div>;
  }

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <div style={{ fontSize: 14, color: T.red, marginBottom: 16 }}>{error}</div>
        <Btn variant="primary" onClick={loadRatings}>Retry</Btn>
      </div>
    );
  }

  const distribution = stats?.distribution || [];

  return (
    <>
      {/* Banner */}
      <div style={{ background: T.greenSoft, border: "1px solid rgba(10,160,110,0.15)", borderRadius: T.radius, padding: "14px 20px", marginBottom: 24, display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ fontSize: 16 }}>✓</span>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.green }}>Star Ratings — Connected via Pylon</div>
          <div style={{ fontSize: 12, color: T.textMuted }}>Showing {ratings.length} ratings from the 500 most recent closed issues. Data refreshes on page load.</div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <Btn sm variant="ghost" onClick={loadRatings}>Refresh</Btn>
        </div>
      </div>

      {/* KPI tiles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginBottom: 24 }}>
        <div style={{ background: T.surface, border: "none", borderRadius: T.radius, padding: "18px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <div style={{ fontSize: 11.5, color: T.textDim, fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Average Rating</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span style={{ fontSize: 28, fontWeight: 700, fontFamily: T.mono, color: T.orange }}>{stats?.avgRating || "—"}</span>
            {stats?.avgRating && <span style={{ fontSize: 16, color: T.orange }}>{"★".repeat(Math.round(stats.avgRating))}{"☆".repeat(5 - Math.round(stats.avgRating))}</span>}
          </div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>Out of 5.0</div>
        </div>
        <div style={{ background: T.surface, border: "none", borderRadius: T.radius, padding: "18px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <div style={{ fontSize: 11.5, color: T.textDim, fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Satisfaction (4+ Stars)</div>
          <div style={{ fontSize: 28, fontWeight: 700, fontFamily: T.mono, color: T.green }}>{stats?.satisfaction ?? "—"}%</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>Rated 4 or above</div>
        </div>
        <div style={{ background: T.surface, border: "none", borderRadius: T.radius, padding: "18px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <div style={{ fontSize: 11.5, color: T.textDim, fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Total Ratings</div>
          <div style={{ fontSize: 28, fontWeight: 700, fontFamily: T.mono, color: T.blue }}>{stats?.total ?? 0}</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>All time from Pylon</div>
        </div>
        <div style={{ background: T.surface, border: "none", borderRadius: T.radius, padding: "18px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <div style={{ fontSize: 11.5, color: T.textDim, fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Organizations</div>
          <div style={{ fontSize: 28, fontWeight: 700, fontFamily: T.mono, color: T.accent }}>{allOrgs.length}</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>Unique orgs submitting ratings</div>
        </div>
      </div>

      {/* Distribution + By Org */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 16 }}>Score Distribution</div>
          {distribution.map(({ stars, count, pct }) => {
            const barColor = stars >= 4 ? T.green : stars === 3 ? T.orange : T.red;
            return (
              <div key={stars} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: 11, width: 70, textAlign: "right", color: T.orange, whiteSpace: "nowrap", flexShrink: 0 }}>{"★".repeat(stars)}{"☆".repeat(5 - stars)}</span>
                <div style={{ flex: 1, background: T.surface2, borderRadius: 4, height: 10, minWidth: 0 }}>
                  <div style={{ background: barColor, height: 10, borderRadius: 4, width: `${pct}%`, transition: "width 0.5s" }} />
                </div>
                <span style={{ fontSize: 12, color: T.textMuted, width: 80, textAlign: "right", flexShrink: 0, whiteSpace: "nowrap" }}>{count} ({pct}%)</span>
              </div>
            );
          })}
        </div>
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, padding: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 16 }}>By Organization</div>
          {(() => {
            const orgCounts = {};
            ratings.forEach((r) => {
              if (r.org) orgCounts[r.org] = (orgCounts[r.org] || 0) + 1;
            });
            const sorted = Object.entries(orgCounts).sort(([, a], [, b]) => b - a).slice(0, 8);
            const max = sorted.length > 0 ? sorted[0][1] : 1;
            if (sorted.length === 0) return <div style={{ fontSize: 12, color: T.textDim, padding: "20px 0", textAlign: "center" }}>No org data available</div>;
            return sorted.map(([org, count]) => (
              <div key={org} style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                  <span style={{ color: T.text }}>{org}</span>
                  <span style={{ color: T.textMuted }}>{count}</span>
                </div>
                <div style={{ background: T.surface2, borderRadius: 4, height: 6 }}>
                  <div style={{ background: T.accent, height: 6, borderRadius: 4, width: `${(count / max) * 100}%`, transition: "width 0.5s" }} />
                </div>
              </div>
            ));
          })()}
        </div>
      </div>

      {/* Filters */}
      {(() => {
        const pillDrop = {
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "7px 14px", borderRadius: 20,
          border: `1px solid ${T.border}`,
          background: T.surface, color: T.text,
          fontSize: 13, fontWeight: 500, fontFamily: T.font,
          cursor: "pointer", position: "relative",
        };
        const hiddenSelect = {
          position: "absolute", inset: 0, opacity: 0, cursor: "pointer",
          width: "100%", height: "100%", fontSize: 13,
        };
        const chevron = <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={T.textDim} strokeWidth="2.5"><path d="M6 9l6 6 6-6"/></svg>;
        const selPill = {
          display: "inline-flex", alignItems: "center", gap: 4,
          padding: "3px 10px", borderRadius: 12,
          background: T.accentSoft, color: T.accent,
          fontSize: 11, fontWeight: 500,
        };
        const xBtn = { background: "none", border: "none", color: T.accent, cursor: "pointer", fontSize: 11, lineHeight: 1, padding: "0 0 0 2px", fontWeight: 600 };
        const hasFilters = scoreFilter || orgFilter;
        return (
          <>
            <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
              <div style={pillDrop}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                <span>Score</span>
                {chevron}
                <select style={hiddenSelect} value="" onChange={(e) => { if (e.target.value) { setScoreFilter(e.target.value); setPage(1); } }}>
                  <option value="">Select score...</option>
                  {[5, 4, 3, 2, 1].map((s) => <option key={s} value={s}>{s} Star{s !== 1 ? "s" : ""}</option>)}
                </select>
              </div>
              <div style={pillDrop}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                <span>Organization</span>
                {chevron}
                <select style={hiddenSelect} value="" onChange={(e) => { if (e.target.value) { setOrgFilter(e.target.value); setPage(1); } }}>
                  <option value="">Select org...</option>
                  {allOrgs.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>
            {hasFilters && (
              <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
                {scoreFilter && (
                  <span style={selPill}>{scoreFilter} Star{scoreFilter !== "1" ? "s" : ""} <button style={xBtn} onClick={() => { setScoreFilter(""); setPage(1); }}>✕</button></span>
                )}
                {orgFilter && (
                  <span style={selPill}>{orgFilter} <button style={xBtn} onClick={() => { setOrgFilter(""); setPage(1); }}>✕</button></span>
                )}
                <button style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", fontSize: 11, fontFamily: T.font }} onClick={() => { setScoreFilter(""); setOrgFilter(""); setPage(1); }}>Clear all</button>
              </div>
            )}
            <div style={{ marginBottom: 16 }}>
              <input style={S.filterInput} placeholder="Search ratings..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
            </div>
          </>
        );
      })()}

      {/* Ratings table */}
      <div style={S.tableWrap}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>{["Date", "Score", "User", "Organization", "Requester", "Title"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {paged.map((r) => (
              <tr key={r.id} style={{ cursor: "pointer", transition: "background 0.1s" }} onMouseEnter={(e) => (e.currentTarget.style.background = T.accentSoft)} onMouseLeave={(e) => (e.currentTarget.style.background = "")} onClick={() => setDrawer(r)}>
                <td style={{ ...S.td, fontSize: 12, color: T.textMuted }}>{fmtDate(r.created_at)}</td>
                <td style={S.td}>
                  {r.score ? (
                    <span style={{ color: T.orange }}>{"★".repeat(r.score)}{"☆".repeat(5 - r.score)}</span>
                  ) : (
                    <span style={{ color: T.textDim }}>—</span>
                  )}
                </td>
                <td style={{ ...S.td, fontWeight: 500 }}>{r.rated_user || "—"}</td>
                <td style={{ ...S.td, fontSize: 12 }}>{r.org || "—"}</td>
                <td style={{ ...S.td, fontSize: 12, color: T.textMuted }}>{r.requester || "—"}</td>
                <td style={{ ...S.td, maxWidth: 250, fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.title}</td>
              </tr>
            ))}
            {paged.length === 0 && (
              <tr><td colSpan={6} style={{ ...S.td, textAlign: "center", color: T.textDim, padding: 40 }}>No ratings found</td></tr>
            )}
          </tbody>
        </table>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderTop: `1px solid ${T.border}` }}>
          <span style={{ fontSize: 12, color: T.textMuted }}>
            Showing {filtered.length > 0 ? (currentPage - 1) * perPage + 1 : 0}–{Math.min(currentPage * perPage, filtered.length)} of {filtered.length} ratings
          </span>
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <Btn sm variant="ghost" onClick={() => setPage((p) => Math.max(1, p - 1))} style={{ opacity: currentPage <= 1 ? 0.4 : 1 }}>Prev</Btn>
            <span style={{ fontSize: 12, color: T.textMuted, padding: "0 8px" }}>Page {currentPage} of {totalPages}</span>
            <Btn sm variant="ghost" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} style={{ opacity: currentPage >= totalPages ? 0.4 : 1 }}>Next</Btn>
          </div>
        </div>
      </div>

      {/* Rating detail drawer */}
      <Drawer
        open={!!drawer}
        title={drawer ? `Rating #${drawer.number}` : ""}
        onClose={() => setDrawer(null)}
        footer={
          <div style={{ display: "flex", gap: 8 }}>
            {drawer?.link && <Btn variant="primary" onClick={() => window.open(drawer.link, "_blank")}>Open in Pylon</Btn>}
            <Btn variant="ghost" onClick={() => setDrawer(null)}>Close</Btn>
          </div>
        }
      >
        {drawer && (() => {
          const r = drawer;
          const scoreColor = SCORE_COLORS[r.score] || T.textDim;
          return (
            <>
              {/* Score hero */}
              <div style={{ textAlign: "center", padding: "20px 0 24px", marginBottom: 20 }}>
                <div style={{ fontSize: 36, color: T.orange, letterSpacing: 4, marginBottom: 8 }}>
                  {r.score ? <>{"★".repeat(r.score)}{"☆".repeat(5 - r.score)}</> : "—"}
                </div>
                <div style={{ fontSize: 24, fontWeight: 700, fontFamily: T.mono, color: scoreColor }}>{r.score ? `${r.score} / 5` : "No score"}</div>
                {r.score && <div style={{ marginTop: 6 }}><Badge variant={SCORE_BADGES[r.score]}>{SCORE_LABELS[r.score]}</Badge></div>}
              </div>

              <div style={{ height: 1, background: T.border, marginBottom: 20 }} />

              {/* Details */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: T.accent, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Rated User</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 34, height: 34, borderRadius: "50%", background: T.accentSoft, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 600, color: T.accent }}>
                      {(r.rated_user || "?")[0].toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{r.rated_user || "—"}</div>
                      {r.org && <div style={{ fontSize: 11, color: T.textMuted }}>{r.org}</div>}
                    </div>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: T.green, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Submitted By</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 34, height: 34, borderRadius: "50%", background: T.greenSoft, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 600, color: T.green }}>
                      {(r.requester || "?")[0].toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{r.requester || "—"}</div>
                      {r.requester_email && r.requester_email !== r.requester && <div style={{ fontSize: 11, color: T.textMuted }}>{r.requester_email}</div>}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
                <div style={{ borderRadius: T.radiusSm, padding: "12px 14px", border: `1px solid ${T.border}` }}>
                  <div style={{ fontSize: 10, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 3 }}>Date</div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{fmtDate(r.created_at)}</div>
                </div>
                <div style={{ borderRadius: T.radiusSm, padding: "12px 14px", border: `1px solid ${T.border}` }}>
                  <div style={{ fontSize: 10, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 3 }}>Organization</div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{r.org || "—"}</div>
                </div>
              </div>

              {/* Tags */}
              {(r.tags || []).length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Tags</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {r.tags.map((tag) => (
                      <span key={tag} style={{ display: "inline-block", padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 500, background: T.accentSoft, color: T.accent }}>{tag}</span>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ height: 1, background: T.border, marginBottom: 20 }} />

              <DrawerSection>Issue Title</DrawerSection>
              <div style={{ fontSize: 13, lineHeight: 1.6, color: T.text, marginBottom: 16 }}>{r.title}</div>

              {r.description && (
                <>
                  <DrawerSection>Description</DrawerSection>
                  <div style={{ background: T.surface2, borderRadius: T.radiusSm, borderLeft: `3px solid ${T.blue}`, padding: 16, fontSize: 13, lineHeight: 1.7, color: T.text, marginBottom: 16, whiteSpace: "pre-wrap" }}>
                    {r.description}
                  </div>
                </>
              )}

              {r.assignee && (
                <div style={{ fontSize: 12, color: T.textMuted }}>Assigned to: <span style={{ fontWeight: 500, color: T.text }}>{r.assignee}</span></div>
              )}
            </>
          );
        })()}
      </Drawer>
    </>
  );
}
